import React, { useState } from 'react';
import { PlayerProfile, IdolRank } from '../types';
import { Coffee, Award, Leaf, Mic, DollarSign, Users, Briefcase } from 'lucide-react';
import { UPGRADE_PRICES, RANKS } from '../constants';
import { ExitButton } from './ExitButton';
import { Notification } from './NotificationSystem';

interface IdolCafeProps {
    player: PlayerProfile;
    updatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: Notification['type']) => void;
    onCompleteMission: (missionId: string) => void;
}

export const IdolCafe: React.FC<IdolCafeProps> = ({ player, updatePlayer, onExit, onNotify, onCompleteMission }) => {
    const [hasEndorsement, setHasEndorsement] = useState(false);
    const rumi = player.group.members.find(m => m.id === 'RUMI');
    const hasVocalDebuff = rumi && (rumi.vocalDebuff || 0) > 0;

    const allSpiritFull = player.group.members.every(m => m.spirit >= m.maxSpirit);

    const activeCollabMission = player.activeContracts.find(c => c.type === 'COLLAB' && c.destination === player.currentCity);
    const activeEndorsementMission = player.activeContracts.find(c => c.type === 'ENDORSEMENT' && c.destination === player.currentCity);
    const activeTourDateMission = player.activeContracts.find(c => c.type === 'TOUR_DATE' && c.destination === player.currentCity);

    const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
    const multiplier = rankData?.multiplier || 1;
    const endorsementReward = UPGRADE_PRICES.ENDORSEMENT * multiplier;

    const signEndorsement = () => {
        if (!hasEndorsement) {
            updatePlayer({
                cash: player.cash + endorsementReward,
                fans: player.fans + (50000 * multiplier)
            });
            setHasEndorsement(true);
            onNotify(`Endorsement deal signed! You received $${endorsementReward.toLocaleString()} and gained new fans.`, 'success');
        }
    };

    const relax = () => {
        const COST = UPGRADE_PRICES.RELAX_AT_CAFE;
        if (player.cash >= COST) {
            if (allSpiritFull) {
                onNotify("The group is already fully rested and in high spirits!", 'info');
                return;
            }

            updatePlayer({
                cash: player.cash - COST,
                group: {
                    ...player.group,
                    members: player.group.members.map(m => ({ ...m, spirit: Math.min(m.maxSpirit, m.spirit + 5) }))
                }
            });
            onNotify("A relaxing break. The group's spirit is lifted. (+5 Spirit for all)", 'success');
        } else {
            onNotify("Not enough cash to order for the whole group.", 'error');
        }
    };

    const vocalTherapy = () => {
        const COST = 15000;
        if (player.cash >= COST && hasVocalDebuff) {
            updatePlayer({
                cash: player.cash - COST,
                group: {
                    ...player.group,
                    members: player.group.members.map(m =>
                        m.id === 'RUMI' ? { ...m, vocalDebuff: 0 } : m
                    )
                }
            });
            onNotify("Rumi drank the special Ginseng Honey Tea. Her voice feels restored!", 'success');
        }
    };

    return (
        <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-8 relative overflow-y-auto">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-slate-950 to-slate-950"></div>
            <ExitButton onExit={onExit} />

            <div className="relative z-10 w-full max-w-4xl glass-panel p-8 rounded-lg">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-heading text-cyan-400 mb-2">IDOL CAFE</h2>
                    <div className="text-slate-400 text-sm">A place for fans and idols to connect.</div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-base">
                    <div className="bg-slate-800/50 p-6 rounded-md border border-slate-700 hover:border-cyan-500 transition-colors flex flex-col items-center text-center">
                        <Award size={48} className="text-cyan-400 mb-4" />
                        <h3 className="text-lg font-bold font-heading text-slate-100 mb-2">Sign Endorsement</h3>
                        <p className="text-slate-400 mb-4">A one-time deal for a big cash payout.</p>
                        <button
                            onClick={signEndorsement}
                            disabled={hasEndorsement}
                            className="w-full py-2 bg-slate-700 border border-slate-600 hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-50 disabled:border-slate-700 text-slate-200 font-bold rounded-md"
                        >
                            {hasEndorsement ? 'SIGNED' : `ACCEPT DEAL (+$${(endorsementReward / 1000000).toFixed(1)}M)`}
                        </button>
                    </div>

                    <div className="bg-slate-800/50 p-6 rounded-md border border-slate-700 hover:border-cyan-500 transition-colors flex flex-col items-center text-center">
                        <Coffee size={48} className="text-cyan-400 mb-4" />
                        <h3 className="text-lg font-bold font-heading text-slate-100 mb-2">Relax & Unwind</h3>
                        <p className="text-slate-400 mb-4">Restore a small amount of Spirit for the group.</p>
                        <button
                            onClick={relax}
                            disabled={player.cash < UPGRADE_PRICES.RELAX_AT_CAFE || allSpiritFull}
                            className="w-full py-2 bg-slate-700 border border-slate-600 hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 text-slate-200 font-bold rounded-md"
                        >
                            {allSpiritFull ? 'SPIRIT FULL' : `ORDER ($${UPGRADE_PRICES.RELAX_AT_CAFE})`}
                        </button>
                    </div>

                    <div className={`bg-slate-800/50 p-6 rounded-md border border-slate-700 hover:border-cyan-500 transition-colors flex flex-col items-center text-center ${!hasVocalDebuff && 'opacity-50 grayscale'}`}>
                        <Leaf size={48} className="text-cyan-400 mb-4" />
                        <h3 className="text-lg font-bold font-heading text-slate-100 mb-2">Vocal Rest Tea</h3>
                        <p className="text-slate-400 mb-4">A special herbal remedy for a strained voice.</p>
                        <button
                            onClick={vocalTherapy}
                            disabled={player.cash < 15000 || !hasVocalDebuff}
                            className="w-full py-2 bg-slate-700 border border-slate-600 hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 text-slate-200 font-bold rounded-md"
                        >
                            {hasVocalDebuff ? 'BREW ($15,000)' : 'NOT NEEDED'}
                        </button>
                    </div>
                </div>

                {activeCollabMission && (
                    <div className="mt-8 bg-purple-900/30 border-2 border-purple-500 p-6 rounded-lg text-center animate-pulse">
                        <h3 className="text-xl font-heading text-purple-300 mb-2 flex items-center justify-center gap-2"><Mic /> Special Event: {activeCollabMission.title}</h3>
                        <p className="text-purple-200/80 mb-2">{activeCollabMission.description}</p>

                        <div className="flex justify-center gap-6 mb-6">
                            <div className="flex items-center gap-2 text-green-400 font-mono font-bold">
                                <DollarSign size={16} /> +{((activeCollabMission?.reward || 0) * multiplier).toLocaleString()}
                                <span className="w-1 h-1 bg-slate-600 rounded-full mx-1"></span>
                                <Users size={16} /> +{((activeCollabMission?.fanReward || 0) * multiplier).toLocaleString()}
                            </div>
                        </div>

                        <button
                            onClick={() => onCompleteMission(activeCollabMission.id)}
                            className="px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg shadow-lg transform transition-transform hover:scale-105 active:scale-95"
                        >
                            {activeCollabMission.buttonDescriptor || 'Host Secret Fan Meet'}
                        </button>
                    </div>
                )}

                {activeEndorsementMission && (
                    <div className="mt-8 bg-cyan-900/30 border-2 border-cyan-500 p-6 rounded-lg text-center animate-pulse">
                        <h3 className="text-xl font-heading text-cyan-300 mb-2 flex items-center justify-center gap-2"><Briefcase className="text-cyan-400" /> Special Opportunity: {activeEndorsementMission.title}</h3>
                        <p className="text-cyan-200/80 mb-2">{activeEndorsementMission.description}</p>

                        <div className="flex justify-center gap-6 mb-6">
                            <div className="flex items-center gap-2 text-green-400 font-mono font-bold">
                                <DollarSign size={16} /> +{(activeEndorsementMission.reward * multiplier).toLocaleString()}
                            </div>
                            <div className="flex items-center gap-2 text-pink-400 font-mono font-bold">
                                <Users size={16} /> +{(activeEndorsementMission.fanReward * multiplier).toLocaleString()}
                            </div>
                        </div>

                        <button
                            onClick={() => onCompleteMission(activeEndorsementMission.id)}
                            className="px-8 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg shadow-lg transform transition-transform hover:scale-105 active:scale-95"
                        >
                            {activeEndorsementMission.buttonDescriptor || 'Sign Endorsement Deal'}
                        </button>
                        {activeEndorsementMission.id === 'mission_la_endorsement' && (
                            <p className="mt-4 text-cyan-400 text-xs italic tracking-wider uppercase">"The energy here is... mystical." - Zoey</p>
                        )}
                    </div>
                )}

                <div className="mt-8 p-4 bg-slate-900/50 rounded-md text-center">
                    <p className="text-slate-500 text-sm italic">"I heard the Saja Boys are having a fan meet soon... might be a good chance to see what they're up to."</p>
                </div>
            </div>
        </div>
    );
};